import { createSlice } from "@reduxjs/toolkit";

// ─── State ───────────────────────────────────────────────────────────────────

interface DataReadinessState {
  /**
   * Becomes `true` once `RefreshFilterAndSegmentOnMarketChange` has finished
   * its **initial** segments + filters fetch for the active group.
   *
   * `selectionListener` checks this flag in its `setPendingSelection` handler.
   * If the flag is still `false` (data not yet loaded) the handler bails out
   * early — keeping the pending selection intact in the store — so that the
   * prune cycle does not evaluate against an empty segments/filters store and
   * inadvertently remove segment IDs that were in the original URL.
   *
   * Once `markSegmentAndFilterFetched` is dispatched the listener's second
   * handler runs the full prune cycle against the now-populated store.
   */
  isSegmentAndFilterFetched: boolean;
}

const initialState: DataReadinessState = {
  isSegmentAndFilterFetched: false,
};

// ─── Slice ───────────────────────────────────────────────────────────────────

export const dataReadinessSlice = createSlice({
  name: "dataReadiness",
  initialState,
  reducers: {
    /**
     * Signal that segments **and** filters have been fetched for the first
     * time.  Dispatched by `RefreshFilterAndSegmentOnMarketChange` at the end
     * of `getResponse()` when `!isMarketUpdatedAfterFirstTime`.
     */
    markSegmentAndFilterFetched: (state) => {
      state.isSegmentAndFilterFetched = true;
    },

    /**
     * Reset the readiness flag.
     *
     * Call this before starting a new active-group fetch so that any
     * `setPendingSelection` dispatched during the loading window is held again.
     */
    resetDataReadiness: (state) => {
      state.isSegmentAndFilterFetched = false;
    },
  },
});

export const { markSegmentAndFilterFetched, resetDataReadiness } =
  dataReadinessSlice.actions;

export default dataReadinessSlice.reducer;